// ═══════════════════════════════════════════════════════════════════
//  SearchOverlay — Search Canvas integration on the Minimap.
// ═══════════════════════════════════════════════════════════════════
//
//  Phase G of MINIMAP_ATLAS_PLAN. Highlights blocks matched by the
//  Search Canvas LiveFilterContext and renders a probe-trail dot
//  trail. Pure component — caller passes matched ids + trail ids;
//  the LiveFilter subscription happens upstream so we keep the
//  minimap free of context coupling.
//
// ═══════════════════════════════════════════════════════════════════

import { useNavigate } from 'react-router-dom';
import type { WorkflowBlock, BlockDimensions } from '@/types/workflow';

export interface SearchOverlayProps {
  matchedBlockIds: ReadonlySet<string>;
  probeTrailBlockIds?: readonly string[];
  /** When non-empty AND matchedBlockIds is empty, render a "no matches → /search" pill. */
  noMatchesQuery?: string;
  blocks: WorkflowBlock[];
  blockDimensions?: Record<string, BlockDimensions>;
  toScreenX: (x: number) => number;
  toScreenY: (y: number) => number;
  toScreenSize: (s: number) => number;
  scale: number;
  width: number;
  height: number;
}

export function SearchOverlay({
  matchedBlockIds,
  probeTrailBlockIds = [],
  noMatchesQuery,
  blocks,
  blockDimensions,
  toScreenX,
  toScreenY,
  toScreenSize,
  scale,
  width,
  height,
}: SearchOverlayProps) {
  const navigate = useNavigate();
  const showNoMatchPill =
    noMatchesQuery && noMatchesQuery.trim().length > 0 && matchedBlockIds.size === 0;

  // Build trail dot positions, mapping each id to its block center.
  const trailDots = probeTrailBlockIds
    .map((id) => blocks.find((b) => b.id === id))
    .filter((b): b is WorkflowBlock => !!b);

  const matchedRects = blocks
    .filter((b) => matchedBlockIds.has(b.id))
    .map((b) => {
      const w = blockDimensions?.[b.id]?.width ?? 200;
      const h = blockDimensions?.[b.id]?.height ?? 80;
      return {
        id: b.id,
        x: toScreenX(b.x - w / 2) * scale,
        y: toScreenY(b.y - h / 2) * scale,
        w: toScreenSize(w) * scale,
        h: toScreenSize(h) * scale,
      };
    });

  if (matchedRects.length === 0 && trailDots.length === 0 && !showNoMatchPill) return null;

  return (
    <>
      {(matchedRects.length > 0 || trailDots.length > 0) && (
        <svg
          className="absolute inset-0 pointer-events-none"
          width={width}
          height={height}
          data-fw-minimap-search-overlay="true"
        >
          {matchedRects.map((r) => (
            <rect
              key={`hit-${r.id}`}
              x={r.x}
              y={r.y}
              width={Math.max(2, r.w)}
              height={Math.max(2, r.h)}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth={1.5}
              data-fw-minimap-hit={r.id}
            />
          ))}
          {trailDots.map((b, i) => (
            <circle
              key={`trail-${b.id}-${i}`}
              cx={toScreenX(b.x) * scale}
              cy={toScreenY(b.y) * scale}
              r={2}
              fill="hsl(var(--primary))"
              opacity={Math.max(0.25, 1 - i * 0.15)}
              data-fw-minimap-trail={b.id}
            />
          ))}
        </svg>
      )}

      {showNoMatchPill && (
        <button
          type="button"
          onClick={() => {
            const params = new URLSearchParams({ q: noMatchesQuery, scope: 'all' });
            navigate(`/search?${params.toString()}`);
          }}
          className="absolute bottom-1 right-1 rounded-full bg-amber-500/85 px-2 py-0.5 text-[9px] font-medium text-amber-950 shadow-md hover:bg-amber-500"
          aria-label={`No matches in this sheet — open search canvas for "${noMatchesQuery}"`}
        >
          → search canvas
        </button>
      )}
    </>
  );
}
